import React, { useCallback, useEffect, useState } from 'react';
import { getUrlParam, isEmpty } from '../../../../../helpers/common';
import AppLayout from '../../../../../component/appLayout';
import TemplateHeader from '../../../../../component/templateHeader';
import TasksPage from '../../../../project/tasks/taskPage';
import { useLocation, useParams } from 'react-router-dom';
import { Group, TitleSetting } from '../styles';
import { getProjectTemplateList, saveProjectTemplateDetails } from '../../../../../services/settingServices';
import TemplateSettingCards from '../../../../../component/projectSetting/templateSettingCard';
import { rootStore } from '../../../../../mobx/rootStore';
import { observer } from 'mobx-react-lite';
import BottomSubNavigation from '../../../../../component/bottomNavigation/subNavigation';
import Modal from '../../../../../component/modals/modal';
import CreateTaskModalV2 from '../../../../../component/modals/createTaskModal';
import BottomNavigationItem from '../../../../../component/bottomNavigation/subNavigation/bottomNavigationItem';
import { convertProjectTemplateModelToInterface } from '../../../../../mobx/common';
import { trackAnalyticActivity } from '../../../../../services/analyticsService';
import { PROJECT_ANALYTICS } from '../../../../../global/analyticsConstants';

const TemplateDetails: React.FC = () => {
  // States
  const [loading, setLoading] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);

  //Mobx store variables
  const { projectTemplateStore, workspaceStore, taskStore, appStore } = rootStore;
  const { currentProjectTemplate } = projectTemplateStore;
  const { currentWorkspace: workspace } = workspaceStore;
  const { createTaskInput } = taskStore;
  const { themeMode } = appStore || {};

  // Other variables
  const params: { id: string } = useParams();
  const location = useLocation();
  const tab = getUrlParam(location.search, 'tab');

  // Fetches template details from the server
  const loadTemplateDetails = useCallback(async () => {
    if (!isEmpty(workspace?.id) && !isEmpty(params?.id)) {
      await getProjectTemplateList(workspace?.id, false);
      rootStore.projectTemplateStore.setCurrentProjectTemplate(params?.id);
    }
  }, [params?.id, workspace]);

  useEffect(() => {
    loadTemplateDetails();
  }, [loadTemplateDetails]);

  // Updates multi-assignee setting for the template
  const onUpdateMultiAssignee = useCallback(async (value: boolean) => {
    rootStore.projectTemplateStore.currentProjectTemplate?.setMultiAssignee(value);
  }, []);

  // Updates status settings and initializes default statuses if needed
  const onUpdateStatus = useCallback(
    (value: boolean) => {
      if (!currentProjectTemplate?.statusData || currentProjectTemplate?.statusData?.length === 0) {
        let defaultStatus = [
          {
            _id: 'xi8KbkF-NXQ5bMPZ0efQx1',
            Type: 1,
            Name: 'Backlog',
            Default: true,
            Index: 1720862851642
          },
          {
            _id: 'xi8GbkF-NXQ5bMPZ0efQx2',
            Type: 2,
            Name: 'Todo',
            Index: 1720862851642
          },
          {
            _id: 'xi8DbkF-NXQ5bMPZ0efQx3',
            Type: 3,
            Name: 'In Progress',
            Index: 1720862851642
          },
          {
            _id: 'xi8BbkF-NXQ5bMPZ0efQx4',
            Type: 4,
            Name: 'In Review',
            Index: 1720862851642
          },
          {
            _id: 'xi8RbkF-NXQ5bMPZ0efQx5',
            Type: 5,
            Name: 'Done',
            Index: 1720862851642
          }
        ];
        rootStore.projectTemplateStore.currentProjectTemplate?.setStatusData(defaultStatus);
      }
      rootStore.projectTemplateStore.currentProjectTemplate?.setStatusEnabled(value);
    },
    [currentProjectTemplate]
  );

  // Updates estimation setting for the template
  const onUpdateEstimate = useCallback(
    async (value: boolean) => {
      currentProjectTemplate?.setEstimate(value);
    },
    [currentProjectTemplate]
  );

  // Updates priority settings for the template
  const onUpdatePriority = useCallback(
    async (value: boolean) => {
      currentProjectTemplate?.setPriorityEnabled(value);
    },
    [currentProjectTemplate]
  );

  // Updates group settings for the template
  const onUpdateGroup = useCallback(
    async (value: boolean) => {
      currentProjectTemplate?.setGroupEnabled(value);
    },
    [currentProjectTemplate]
  );

  // Updates label settings for the template
  const onUpdateLabels = useCallback(
    async (value: boolean) => {
      currentProjectTemplate?.setLabelsEnabled(value);
    },
    [currentProjectTemplate]
  );

  const handleOnClick = useCallback(async () => {
    if (currentProjectTemplate?.id) {
      createTaskInput?.setProject(currentProjectTemplate?.id, true);
      createTaskInput?.setPriority(currentProjectTemplate?.priority?.default || 0);
      createTaskInput?.setGroupId(currentProjectTemplate?.taskGroups?.[0]?.id || '');
      setIsModalOpen(true);
    }
  }, [
    createTaskInput,
    currentProjectTemplate?.id,
    currentProjectTemplate?.priority?.default,
    currentProjectTemplate?.taskGroups
  ]);

  const handleCloseModal = () => {
    setIsModalOpen(false);
  };
  const onClickSave = useCallback(async () => {
    if (currentProjectTemplate) {
      const cloneDetails = convertProjectTemplateModelToInterface(currentProjectTemplate);
      trackAnalyticActivity(PROJECT_ANALYTICS.TEMPLATE_DATA_UPDATED);
      await saveProjectTemplateDetails(cloneDetails);
      currentProjectTemplate?.setUpdated(false);
    }
  }, [currentProjectTemplate]);

  return (
    <>
      <AppLayout>
        <TemplateHeader isSettingPage={tab === 'setting'} />
        {tab === 'setting' ? (
          <Group>
            <TitleSetting>Project Settings</TitleSetting>
            <TemplateSettingCards
              isTemplateProject={true}
              currentProject={currentProjectTemplate!}
              themeMode={themeMode}
              onUpdateEstimate={(value) => onUpdateEstimate(value)}
              onUpdatePriority={(value) => onUpdatePriority(value)}
              onUpdateLabels={(value) => onUpdateLabels(value)}
              taskGroups={currentProjectTemplate?.taskGroups || []}
              loading={loading}
              paramsId={params?.id}
              setLoading={(value: boolean) => setLoading(value)}
              loadData={loadTemplateDetails}
              onUpdateStatus={onUpdateStatus}
              onUpdateGroup={onUpdateGroup}
              onUpdateMultiAssignee={(value) => onUpdateMultiAssignee(value)}
              loadingButton={{
                group: false,
                status: false,
                priority: false,
                label: false,
                multiAssignee: false,
                estimation: false,
                milestone: false
              }}
            />
          </Group>
        ) : (
          <TasksPage propsLoading={false} loadData={loadTemplateDetails} isTemplateProject={true} />
        )}
      </AppLayout>
      <BottomSubNavigation
        backNavigationPath='/settings/templates'
        rightElement={
          <>
            {tab !== 'setting' && <BottomNavigationItem iconName='plus-icon' onClick={handleOnClick} />}
            <BottomNavigationItem iconName='save-invoice-icon' onClick={onClickSave} />
          </>
        }
      />
      <Modal open={isModalOpen} maxWidth={'680px'} onClose={handleCloseModal} isResponsiveMaxWidth={true}>
        <CreateTaskModalV2 onCancel={handleCloseModal} context={{ source: 'template' }} />
      </Modal>
    </>
  );
};

export default observer(TemplateDetails);
